/**
 * SimplifyDeveloperPlansTab — third tab on the Developer Plan Management page.
 *
 * Displays Simplify-managed Developer programs proxied through the ECGrid backend.
 * Server-side pagination, sorting, and filtering. Sheet panel surfaces full
 * details and an Extend Trial form (only when the subscription is in `trialing`).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';

import {
  DataTable,
  DataTableColumnHeader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import { NS } from '@/i18n/namespaces';
import { formatDateByPrefs } from '@utils/format-locale';
import { useUserPreferences } from '@/components/features/settings/UserSettings';
import type { SimplifyDeveloperPlan } from '@/application/queries/simplifyDeveloperPlan';

import { useSimplifyDeveloperPlans } from './SimplifyDeveloperPlansTab.hook';
import { useSimplifyDeveloperPlansStore } from './SimplifyDeveloperPlansTab.store';

// ============================================================================
// Helpers
// ============================================================================

function toInputDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function addDays(dateStr: string | null, days: number): Date {
  const base = dateStr ? new Date(dateStr) : new Date();
  const result = new Date(base);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

// ============================================================================
// Sheet
// ============================================================================

interface SimplifyTrialExtensionSheetProps {
  item: SimplifyDeveloperPlan | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExtend: (subscriptionId: string, newDate: string) => Promise<void>;
}

const SimplifyTrialExtensionSheet: React.FC<SimplifyTrialExtensionSheetProps> = ({
  item,
  open,
  onOpenChange,
  onExtend,
}) => {
  const { t } = useTranslation(NS.DEVELOPER_PLAN);
  const { dateFormat, timezoneOffset } = useUserPreferences();
  const extendError = useSimplifyDeveloperPlansStore((s) => s.extendError);
  const extendSuccess = useSimplifyDeveloperPlansStore((s) => s.extendSuccess);

  const [newDate, setNewDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trialEnd = item?.trialEndDate ?? null;
  const minDate = toInputDate(addDays(trialEnd, 1));
  const maxDate = toInputDate(addDays(trialEnd, 30));
  const isTrialing = item?.status?.toLowerCase() === 'trialing';

  const previewDays = (() => {
    if (!newDate || !trialEnd) return null;
    const diff = Math.round(
      (new Date(newDate).getTime() - new Date(trialEnd).getTime()) / 86400000
    );
    return diff > 0 ? diff : null;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item || !newDate) return;
    setIsSubmitting(true);
    try {
      await onExtend(item.subscriptionId, newDate);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fmt = (d: string | null) => formatDateByPrefs(d, dateFormat, timezoneOffset);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:w-[640px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('SimplifyDeveloperPlan_Sheet_Title', 'Simplify Plan Details')}</SheetTitle>
        </SheetHeader>

        {item && (
          <div className="mt-6 space-y-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('SimplifyDeveloperPlan_Sheet_Info', 'Information')}
              </h3>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_DisplayName', 'Display Name')}</span>
                <span className="font-medium">{item.displayName || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_Email', 'Email')}</span>
                <span className="font-medium break-all">
                  {item.email
                    ? <a href={`mailto:${item.email}`} className="text-primary hover:underline">{item.email}</a>
                    : '—'}
                </span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_Company', 'Company')}</span>
                <span className="font-medium">{item.companyName || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_CompanyAddress', 'Company Address')}</span>
                <span className="font-medium">{item.companyAddress || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_ContactName', 'Contact Name')}</span>
                <span className="font-medium">{item.contactName || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_ContactEmail', 'Contact Email')}</span>
                <span className="font-medium break-all">
                  {item.contactEmail
                    ? <a href={`mailto:${item.contactEmail}`} className="text-primary hover:underline">{item.contactEmail}</a>
                    : '—'}
                </span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_Plan', 'Plan')}</span>
                <span className="font-medium">{item.plan || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_Status', 'Status')}</span>
                <span className="font-medium">{item.status || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_SubscriptionId', 'Subscription ID')}</span>
                <span className="font-mono text-xs break-all">{item.subscriptionId || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_CustomerId', 'Customer ID')}</span>
                <span className="font-mono text-xs break-all">{item.customerId || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_ProductId', 'Product ID')}</span>
                <span className="font-mono text-xs break-all">{item.productId || '—'}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_Documents', 'Documents')}</span>
                <span className="font-medium">
                  {`${item.documentsUsage ?? 0}/${item.numberOfDocuments ?? 0}`}
                </span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_StartDate', 'Start Date')}</span>
                <span className="font-medium">{fmt(item.startDate)}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_EndDate', 'End Date')}</span>
                <span className="font-medium">{fmt(item.endDate)}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_TrialStart', 'Trial Start')}</span>
                <span className="font-medium">{fmt(item.trialStartDate)}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_TrialEnd', 'Trial End')}</span>
                <span className="font-medium">{fmt(item.trialEndDate)}</span>

                <span className="text-muted-foreground">{t('SimplifyDeveloperPlan_Sheet_ReferralCode', 'Referral Code')}</span>
                <span className="font-medium">{item.referralCode || '—'}</span>
              </div>
            </div>

            {isTrialing && (
              <div className="space-y-3 border-t pt-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  {t('SimplifyDeveloperPlan_Sheet_ExtendTitle', 'Extend Trial Period')}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="simplifyNewTrialEnd" className="block text-sm font-medium mb-1">
                      {t('SimplifyDeveloperPlan_Sheet_NewDate', 'New Trial End Date')}
                    </label>
                    <input
                      id="simplifyNewTrialEnd"
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      min={minDate}
                      max={maxDate}
                      required
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {previewDays !== null && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {t('SimplifyDeveloperPlan_Sheet_ExtendPreview', 'Extending by {{days}} day(s)').replace('{{days}}', String(previewDays))}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('SimplifyDeveloperPlan_Sheet_ExtendRange', 'Select a date between 1 and 30 days from the current trial end.')}
                    </p>
                  </div>

                  {extendError && (
                    <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                      {extendError.message}
                    </div>
                  )}

                  {extendSuccess && (
                    <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm">
                      {t('SimplifyDeveloperPlan_Sheet_Success', 'Trial period extended successfully!')}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || !newDate}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting && (
                      <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    )}
                    {isSubmitting
                      ? t('SimplifyDeveloperPlan_Sheet_Saving', 'Saving...')
                      : t('SimplifyDeveloperPlan_Sheet_Save', 'Extend Trial')}
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// Tab
// ============================================================================

const STATUS_COLORS: Record<string, string> = {
  trialing: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-orange-100 text-orange-800',
  canceled: 'bg-gray-200 text-gray-700',
  unpaid: 'bg-red-100 text-red-800',
  incomplete: 'bg-blue-100 text-blue-800',
};

export const SimplifyDeveloperPlansTab: React.FC = () => {
  const { t } = useTranslation(NS.DEVELOPER_PLAN);
  const { dateFormat, timezoneOffset } = useUserPreferences();

  const { items, totalCount, loading, error, refetch, extendTrial } = useSimplifyDeveloperPlans();

  const pagination = useSimplifyDeveloperPlansStore((s) => s.pagination);
  const sorting = useSimplifyDeveloperPlansStore((s) => s.sorting);
  const searchText = useSimplifyDeveloperPlansStore((s) => s.searchText);
  const setPagination = useSimplifyDeveloperPlansStore((s) => s.setPagination);
  const setSorting = useSimplifyDeveloperPlansStore((s) => s.setSorting);
  const setSearchText = useSimplifyDeveloperPlansStore((s) => s.setSearchText);
  const openSheet = useSimplifyDeveloperPlansStore((s) => s.openSheet);
  const selectedItem = useSimplifyDeveloperPlansStore((s) => s.selectedItem);
  const sheetOpen = useSimplifyDeveloperPlansStore((s) => s.sheetOpen);
  const closeSheet = useSimplifyDeveloperPlansStore((s) => s.closeSheet);

  const formatDate = useCallback(
    (d: string | null) => formatDateByPrefs(d, dateFormat, timezoneOffset),
    [dateFormat, timezoneOffset]
  );

  const columns = useMemo<ColumnDef<SimplifyDeveloperPlan>[]>(
    () => [
      // Display Name + Email tạm ẩn theo yêu cầu UI; vẫn render trong Sheet detail.
      {
        accessorKey: 'companyName',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('SimplifyDeveloperPlan_Col_Company', 'Company')} />
        ),
        cell: ({ row }) => row.original.companyName || '—',
      },
      {
        accessorKey: 'contactName',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_ContactName', 'Contact Name'),
        cell: ({ row }) => row.original.contactName || '—',
      },
      {
        accessorKey: 'contactEmail',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_ContactEmail', 'Contact Email'),
        cell: ({ row }) =>
          row.original.contactEmail ? (
            <a href={`mailto:${row.original.contactEmail}`} className="text-primary hover:underline">
              {row.original.contactEmail}
            </a>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'plan',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('SimplifyDeveloperPlan_Col_Plan', 'Plan')} />
        ),
        cell: ({ row }) => row.original.plan || '—',
      },
      {
        // Status, dates come from Stripe AFTER backend OrderBy → cannot sort server-side.
        accessorKey: 'status',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_Status', 'Status'),
        cell: ({ row }) => {
          const s = row.original.status ?? 'unknown';
          const color = STATUS_COLORS[s.toLowerCase()] ?? 'bg-gray-100 text-gray-800';
          return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{s}</span>;
        },
      },
      {
        id: 'documents',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_Documents', 'Documents'),
        cell: ({ row }) =>
          `${row.original.documentsUsage ?? 0}/${row.original.numberOfDocuments ?? 0}`,
      },
      {
        accessorKey: 'startDate',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_StartDate', 'Start Date'),
        // Trialing rows surface trial dates; non-trial rows show their actual subscription dates.
        cell: ({ row }) => {
          const isTrialing = row.original.status?.toLowerCase() === 'trialing';
          return formatDate(isTrialing ? row.original.trialStartDate : row.original.startDate);
        },
      },
      {
        accessorKey: 'endDate',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_EndDate', 'End Date'),
        cell: ({ row }) => {
          const isTrialing = row.original.status?.toLowerCase() === 'trialing';
          return formatDate(isTrialing ? row.original.trialEndDate : row.original.endDate);
        },
      },
      {
        accessorKey: 'referralCode',
        enableSorting: false,
        header: () => t('SimplifyDeveloperPlan_Col_ReferralCode', 'Referral Code'),
        cell: ({ row }) => row.original.referralCode || '—',
      },
      {
        id: 'actions',
        enableSorting: false,
        size: 100,
        header: () => (
          <div className="flex h-8 items-center justify-center text-sm font-semibold">
            {t('SimplifyDeveloperPlan_Col_Actions', 'Actions')}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <button
              onClick={() => openSheet(row.original)}
              className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
            >
              {t('SimplifyDeveloperPlan_Action_View', 'View')}
            </button>
          </div>
        ),
      },
    ],
    [t, openSheet, formatDate]
  );

  const tablePagination: PaginationState = {
    pageIndex: pagination.page - 1,
    pageSize: pagination.pageSize,
  };
  const tableSorting: SortingState = sorting.map((s) => ({ id: s.id, desc: s.desc }));

  const pageCount = pagination.pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pagination.pageSize)) : 0;

  const table = useReactTable({
    data: items,
    columns,
    state: {
      pagination: tablePagination,
      sorting: tableSorting,
    },
    pageCount,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(tablePagination) : updater;
      setPagination({ page: next.pageIndex + 1, pageSize: next.pageSize });
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(tableSorting) : updater;
      setSorting(next.map((s) => ({ id: s.id, desc: s.desc })));
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('SimplifyDeveloperPlan_Filter_Search', 'Search company...')}
          className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('SimplifyDeveloperPlan_Filter_Clear', 'Clear')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center justify-between">
          <span>{error.message}</span>
          <button onClick={refetch} className="ml-4 text-xs underline hover:no-underline">
            {t('SimplifyDeveloperPlan_Error_Retry', 'Retry')}
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-max">
          <DataTable
            table={table}
            isLoading={loading}
            showPagination={true}
            headerClassName="[&_th]:bg-gray-100 [&_th]:text-gray-700 dark:[&_th]:bg-gray-800 dark:[&_th]:text-gray-300"
            noResultsText={t('SimplifyDeveloperPlan_NoResults', 'No results found.')}
            loadingText={t('SimplifyDeveloperPlan_Loading', 'Loading...')}
          />
        </div>
      </div>

      <SimplifyTrialExtensionSheet
        item={selectedItem}
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
        onExtend={extendTrial}
      />
    </div>
  );
};
